import React from 'react'
import orderBy from 'lodash.orderby'
import {
  DataGrid,
  DataGridCell,
  DataGridHeader,
  Main
} from '@conserveit/blocks'
import { Direction } from '../../../../utils/columns'
import SortArrow from '../../../SortArrow'
import { sortEvent } from '../../../../utils/eventTracking'
import { TRACKING_EVENT_SORT_BY } from '../../../../context/reports/workflow/constants'
import { ActionListModal } from '../../../ActionListModal'
import { Wrapper, Scrollable, StyleDataGridHeading, GridBadge, Row } from './WorkflowDataGrid.styles'
import EventEmitter from '../../../../utils/eventEmitter'
export interface RowDatum {
  label: string
  totalCount: number
  criticalCount: number
  actionIds?: string[]
}

export interface GridProps {
  records: RowDatum[]
  count: number
  critical: number
}
interface OwnProps {
  groupRowLabel: string,
  trackingTitle: string,
  eventEmitter: EventEmitter
}

type Props = OwnProps & GridProps

interface Sort {
  sortKey: keyof RowDatum
  direction: Direction
}

export interface ModalSettings {
  isOpen: boolean
  actionIds?: string[]
}
export const WorkflowDatagrid: React.FC<Props> = ({
  records,
  count,
  critical,
  groupRowLabel,
  trackingTitle,
  eventEmitter
}) => {

  const [modalSettings, setModalSettings] = React.useState<ModalSettings>({
    isOpen: false,
    actionIds: []
  })

  const [sort, setSort] = React.useState<Sort>({
    sortKey: 'label',
    direction: 'asc'
  })

  const [dataState, setDataState] = React.useState<GridProps>({
    records: records,
    count: count,
    critical: critical
  })
  const componentId = Math.random()
  React.useEffect(() => {
    eventEmitter.register(componentId, 'updateGrid', (data: GridProps) => {
      setDataState(data)
    });

    return () => {
      eventEmitter.deregisterAll(componentId);
    }
  }, [eventEmitter])

  const handleSortBy = React.useCallback((key: keyof RowDatum) => {
    const direction = sort.direction === 'asc' ? 'desc' : 'asc'
    const columnTitle = key === 'label' ? groupRowLabel : key
    sortEvent({
      chartTitle: trackingTitle,
      columnTitle,
      direction
    }, TRACKING_EVENT_SORT_BY)
    setSort({
      sortKey: key,
      direction
    })
  }, [sort.direction, sort.sortKey])

  const openModal = React.useCallback((actionIds?: string[]) => {
    setModalSettings({
      isOpen: true,
      actionIds
    })
  }, [modalSettings])

  return dataState.records.length === 0 ? <Wrapper/> : (
    <Wrapper>
      <Scrollable>
        <DataGrid cellProps={{ type: 'dense', density: 'compact' }}>
          <DataGridHeader>
            <StyleDataGridHeading as='button' onClick={() => handleSortBy('label')} cursor='pointer'>
              <Main>{groupRowLabel}</Main>
              { sort.sortKey === 'label' && <SortArrow direction={sort.direction} />}
            </StyleDataGridHeading>

            <StyleDataGridHeading as='button' onClick={() => handleSortBy('totalCount')} cursor='pointer'>
              <Main>Total</Main> &nbsp; <GridBadge>{dataState.count}</GridBadge>
              { sort.sortKey === 'totalCount' && <SortArrow direction={sort.direction} />}
            </StyleDataGridHeading>

            <StyleDataGridHeading as='button' onClick={() => handleSortBy('criticalCount')} cursor='pointer'>
              <Main>Critical Actions</Main> &nbsp; <GridBadge>{dataState.critical}</GridBadge>
              { sort.sortKey === 'criticalCount' && <SortArrow direction={sort.direction} />}
            </StyleDataGridHeading>
            <StyleDataGridHeading className='right-alignment'>
              <Main>Actions</Main>
            </StyleDataGridHeading>
          </DataGridHeader>
          {orderBy(dataState.records, (record) => record[sort.sortKey], sort.direction).map(({ label, totalCount, criticalCount, actionIds }) => (
            <Row key={`${label}`}>
              <DataGridCell>{ label }</DataGridCell>
              <DataGridCell>{ `${totalCount} Action${totalCount !== 1 ? 's' : ''}` }</DataGridCell>
              <DataGridCell>{
                criticalCount > 0 ? `${criticalCount} Action${criticalCount !== 1 ? 's' : ''}` : null
              }</DataGridCell>
              <DataGridCell className='action-cell'>
                <a className='action-link' onClick={() => openModal(actionIds)}>View <i>Closed</i> Actions</a>
              </DataGridCell>
            </Row>
          ))}
          {/* @TODO: Add footer and show more button */}
        </DataGrid>
      </Scrollable>

      <ActionListModal
        actionIds={modalSettings.actionIds}
        isOpen={modalSettings.isOpen}
        setModalVisibility={setModalSettings}
      />
    </Wrapper>
  )
}
